import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

document.addEventListener('click', handleClick);

/**
 * @param {MouseEvent} event
 */
function handleClick(event) {
  const target = event.target instanceof Element ? event.target : event.target?.parentElement;
  if (!(target instanceof Element)) return;

  const pill = target.closest('.hp-card-atc__pill');
  if (pill instanceof HTMLElement) {
    event.preventDefault();
    event.stopPropagation();
    selectPill(pill);
    return;
  }

  const button = target.closest('.hp-card-atc__button');
  if (!(button instanceof HTMLButtonElement)) return;

  event.preventDefault();
  event.stopPropagation();
  if (button.disabled) return;

  addToCart(button);
}

/**
 * @param {HTMLElement} pill
 */
function selectPill(pill) {
  const root = pill.closest('.hp-card-atc');
  if (!(root instanceof HTMLElement)) return;

  root.querySelectorAll('.hp-card-atc__pill').forEach((item) => item.classList.remove('is-active'));
  pill.classList.add('is-active');

  const button = root.querySelector('.hp-card-atc__button');
  const label = button?.querySelector('.hp-card-atc__label');
  const variantId = pill.getAttribute('data-variant-id');
  const available = pill.getAttribute('data-available') === 'true';
  if (!(button instanceof HTMLButtonElement) || !variantId) return;

  button.setAttribute('data-variant-id', variantId);
  button.setAttribute('data-available', available ? 'true' : 'false');
  button.disabled = !available;
  if (label) {
    label.textContent = available ? root.getAttribute('data-label-add') : root.getAttribute('data-label-sold');
  }

  const card = root.closest('product-card');
  const priceEl =
    card?.querySelector('product-price .price') ||
    card?.querySelector('.price-item__group.price') ||
    card?.querySelector('.price');
  const price = pill.getAttribute('data-price');
  if (priceEl && price) priceEl.textContent = price;
}

/**
 * Same add path as product-form.js: emit cart:lines-update, POST /cart/add,
 * morph the drawer from section HTML. The cart drawer auto-opens once.
 * @param {HTMLButtonElement} button
 */
function addToCart(button) {
  const root = button.closest('.hp-card-atc');
  const label = button.querySelector('.hp-card-atc__label');
  const variantId = button.getAttribute('data-variant-id');
  if (!(root instanceof HTMLElement) || !variantId) return;

  button.disabled = true;
  if (label) label.textContent = root.getAttribute('data-label-adding');

  const formData = new FormData();
  formData.append('id', variantId);
  formData.append('quantity', '1');

  const sectionIds = [];
  document.querySelectorAll('cart-items-component').forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      sectionIds.push(item.dataset.sectionId);
    }
  });
  if (sectionIds.length) formData.append('sections', sectionIds.join(','));

  const deferredEventPromise = CartLinesUpdateEvent.createPromise();
  const addEvent = new CartLinesUpdateEvent({
    action: 'add',
    context: 'product',
    lines: [{ merchandiseId: variantId, quantity: 1 }],
    promise: deferredEventPromise.promise,
  });
  button.dispatchEvent(addEvent);

  const fetchCfg = fetchConfig('javascript', { body: formData });

  fetch(Theme.routes.cart_add_url, {
    ...fetchCfg,
    headers: {
      ...fetchCfg.headers,
      Accept: 'text/html',
    },
  })
    .then((response) => response.json())
    .then(async (response) => {
      if (response.status) {
        button.dispatchEvent(
          new CartErrorEvent({
            error: response.message || 'Add to cart failed',
            code: 'INVALID',
            detail: {
              description: response.description,
              errors: response.errors,
            },
          })
        );

        const ajaxCart = await fetchCart();
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: {
            didError: true,
            items: ajaxCart.items,
            source: 'homepage-card-atc',
            itemCount: 1,
            sections: response.sections,
          },
        });

        if (label) label.textContent = root.getAttribute('data-label-error');
        return;
      }

      const ajaxCart = await fetchCart();
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'homepage-card-atc',
          itemCount: 1,
          sections: response.sections,
          didError: false,
        },
      });

      if (label) label.textContent = root.getAttribute('data-label-added');
    })
    .catch((error) => {
      deferredEventPromise.reject(error);
      if (label) label.textContent = root.getAttribute('data-label-error');
    })
    .finally(() => {
      setTimeout(() => {
        const available = button.getAttribute('data-available') === 'true';
        button.disabled = !available;
        if (label) {
          label.textContent = available
            ? root.getAttribute('data-label-add')
            : root.getAttribute('data-label-sold');
        }
      }, 1200);
    });
}

function fetchCart() {
  return fetch(`${Theme.routes.cart_url}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  }).then((response) => {
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  });
}
